import { useState, useEffect } from 'react';
import { adminApi } from '@/services/admin';
import { AiPrompt, PromptType } from '@/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import toast from 'react-hot-toast';
import { Loader2, Save, FileText, CreditCard, Mic, PenTool, ArrowLeft, Lock, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BrandingSection } from '@/components/admin/BrandingSection';

const PROMPT_CONFIG = {
  TEXT_NOTE: {
    label: 'Notatki tekstowe',
    icon: FileText,
    description: 'Prompt używany do czyszczenia i formatowania notatek tekstowych przez AI',
    placeholder: 'Clean up and improve this meeting note...',
  },
  BUSINESS_CARD: {
    label: 'Wizytówki',
    icon: CreditCard,
    description: 'Prompt używany do ekstrakcji danych z wizytówek (OCR)',
    placeholder: 'Extract information from this business card...',
  },
  VOICE_NOTE: {
    label: 'Notatki głosowe',
    icon: Mic,
    description: 'Prompt używany do przetwarzania transkrypcji notatek głosowych',
    placeholder: 'Process this voice note transcription...',
  },
  HANDWRITTEN_NOTE: {
    label: 'Notatki odręczne',
    icon: PenTool,
    description: 'Prompt używany do przetwarzania notatek odręcznych (OCR)',
    placeholder: 'Process this handwritten note...',
  },
};

interface PromptEditorProps {
  type: PromptType;
  prompt: AiPrompt | null;
  onSave: (type: PromptType, promptText: string, isActive: boolean) => Promise<void>;
  isSaving: boolean;
}

const PromptEditor = ({ type, prompt, onSave, isSaving }: PromptEditorProps) => {
  const [promptText, setPromptText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const config = PROMPT_CONFIG[type];
  const Icon = config.icon;

  useEffect(() => {
    if (prompt) {
      setPromptText(prompt.promptText);
      setIsActive(prompt.isActive);
    }
  }, [prompt]);

  const handleTextChange = (value: string) => {
    setPromptText(value);
    setHasChanges(true);
  };

  const handleActiveChange = (checked: boolean) => {
    setIsActive(checked);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!promptText.trim()) {
      toast.error('Prompt nie może być pusty');
      return;
    }

    try {
      await onSave(type, promptText, isActive);
      setHasChanges(false);
    } catch (error) {
      // Error handled in parent
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Icon className="h-6 w-6 text-muted-foreground" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle>{config.label}</CardTitle>
              <Badge variant={isActive ? 'default' : 'secondary'}>
                {isActive ? 'Aktywny' : 'Nieaktywny'}
              </Badge>
            </div>
            <CardDescription className="mt-1">
              {config.description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active Toggle */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md">
          <input
            type="checkbox"
            id={`active-${type}`}
            checked={isActive}
            onChange={(e) => handleActiveChange(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
            disabled={isSaving}
          />
          <Label htmlFor={`active-${type}`} className="cursor-pointer">
            Użyj tego promptu dla {config.label.toLowerCase()}
          </Label>
        </div>

        {/* Prompt Text */}
        <div className="space-y-2">
          <Label htmlFor={`prompt-${type}`}>Tekst promptu</Label>
          <textarea
            id={`prompt-${type}`}
            value={promptText}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={config.placeholder}
            className="w-full h-64 px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none font-mono"
            disabled={isSaving}
          />
          <p className="text-xs text-muted-foreground">
            Możesz użyć placeholderów: <code className="bg-muted px-1 py-0.5 rounded">{'{{content}}'}</code> dla treści notatki
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-between items-center pt-2">
          <div className="text-sm text-muted-foreground">
            {hasChanges && '• Niezapisane zmiany'}
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Zapisywanie...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Zapisz zmiany
              </>
            )}
          </Button>
        </div>

        {/* Last Updated */}
        {prompt && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Ostatnia aktualizacja: {new Date(prompt.updatedAt).toLocaleString('pl-PL')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminPromptsPage = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Record<PromptType, AiPrompt | null>>({
    TEXT_NOTE: null,
    BUSINESS_CARD: null,
    VOICE_NOTE: null,
    HANDWRITTEN_NOTE: null,
  });
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState<PromptType | null>(null);
  const [activeTab, setActiveTab] = useState<string>('TEXT_NOTE');

  // Password protection state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [superAdminToken, setSuperAdminToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [verifying, setVerifying] = useState(false);

  const handlePasswordSubmit = async () => {
    try {
      setVerifying(true);
      setPasswordError(false);

      const response = await adminApi.verifySuperAdminAccess(password);

      setSuperAdminToken(response.token);
      setTokenExpiresAt(Date.now() + response.expiresIn * 1000);
      setIsUnlocked(true);
      setPassword('');
      toast.success('Dostęp przyznany');
    } catch (error: any) {
      setPasswordError(true);
      toast.error(error.response?.data?.error?.message || 'Nieprawidłowe hasło');
    } finally {
      setVerifying(false);
    }
  };

  const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  const fetchPrompts = async () => {
    if (!superAdminToken) return;

    try {
      setLoading(true);
      const response = await adminApi.getPrompts(superAdminToken);

      // Organize prompts by type
      const promptsByType: Record<PromptType, AiPrompt | null> = {
        TEXT_NOTE: null,
        BUSINESS_CARD: null,
        VOICE_NOTE: null,
        HANDWRITTEN_NOTE: null,
      };

      response.prompts.forEach((prompt) => {
        promptsByType[prompt.promptType] = prompt;
      });

      setPrompts(promptsByType);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sesja wygasła. Zaloguj się ponownie.');
        setIsUnlocked(false);
        setSuperAdminToken(null);
      } else {
        toast.error('Błąd podczas ładowania promptów');
      }
      console.error('Failed to load prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isUnlocked && superAdminToken) {
      fetchPrompts();
    }
  }, [isUnlocked, superAdminToken]);

  const handleSavePrompt = async (
    type: PromptType,
    promptText: string,
    isActive: boolean
  ) => {
    if (!superAdminToken) {
      toast.error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      setIsUnlocked(false);
      return;
    }

    try {
      setSavingType(type);
      const response = await adminApi.updatePrompt(type, { promptText, isActive }, superAdminToken);

      // Update local state
      setPrompts((prev) => ({
        ...prev,
        [type]: response.prompt,
      }));

      toast.success(`Prompt dla ${PROMPT_CONFIG[type].label} został zapisany`);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sesja wygasła. Zaloguj się ponownie.');
        setIsUnlocked(false);
        setSuperAdminToken(null);
      } else {
        toast.error(error.response?.data?.error?.message || 'Błąd podczas zapisywania promptu');
      }
      throw error;
    } finally {
      setSavingType(null);
    }
  };

  // Password protection dialog
  if (!isUnlocked) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                <Lock className="h-8 w-8 text-muted-foreground" />
              </div>
            </div>
            <CardTitle className="text-2xl">Dostęp ograniczony</CardTitle>
            <CardDescription>
              Ta strona wymaga hasła super admina. Wprowadź hasło, aby uzyskać dostęp do edycji promptów AI.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Hasło super admina</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(false);
                }}
                onKeyDown={handlePasswordKeyDown}
                placeholder="Wprowadź hasło..."
                className={passwordError ? 'border-destructive' : ''}
                autoFocus
              />
              {passwordError && (
                <p className="text-sm text-destructive">Nieprawidłowe hasło. Spróbuj ponownie.</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handlePasswordSubmit}
                className="flex-1"
                disabled={!password.trim() || verifying}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Weryfikacja...
                  </>
                ) : (
                  'Odblokuj'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                disabled={verifying}
              >
                Anuluj
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Session expiry check
  const getSessionTimeRemaining = () => {
    if (!tokenExpiresAt) return null;
    const remaining = Math.max(0, tokenExpiresAt - Date.now());
    const minutes = Math.floor(remaining / 60000);
    return minutes;
  };

  const sessionMinutesRemaining = getSessionTimeRemaining();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Prompty AI</h1>
          <p className="text-muted-foreground mt-2">
            Zarządzaj promptami używanymi przez AI do przetwarzania różnych typów notatek
          </p>
          {sessionMinutesRemaining !== null && sessionMinutesRemaining < 10 && (
            <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
              ⏱️ Sesja wygaśnie za {sessionMinutesRemaining} min
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/admin')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Powrót do panelu
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="TEXT_NOTE" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Tekstowe
          </TabsTrigger>
          <TabsTrigger value="BUSINESS_CARD" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Wizytówki
          </TabsTrigger>
          <TabsTrigger value="VOICE_NOTE" className="flex items-center gap-2">
            <Mic className="h-4 w-4" />
            Głosowe
          </TabsTrigger>
          <TabsTrigger value="HANDWRITTEN_NOTE" className="flex items-center gap-2">
            <PenTool className="h-4 w-4" />
            Odręczne
          </TabsTrigger>
          <TabsTrigger value="BRANDING" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="TEXT_NOTE" className="mt-6">
          <PromptEditor
            type="TEXT_NOTE"
            prompt={prompts.TEXT_NOTE}
            onSave={handleSavePrompt}
            isSaving={savingType === 'TEXT_NOTE'}
          />
        </TabsContent>

        <TabsContent value="BUSINESS_CARD" className="mt-6">
          <PromptEditor
            type="BUSINESS_CARD"
            prompt={prompts.BUSINESS_CARD}
            onSave={handleSavePrompt}
            isSaving={savingType === 'BUSINESS_CARD'}
          />
        </TabsContent>

        <TabsContent value="VOICE_NOTE" className="mt-6">
          <PromptEditor
            type="VOICE_NOTE"
            prompt={prompts.VOICE_NOTE}
            onSave={handleSavePrompt}
            isSaving={savingType === 'VOICE_NOTE'}
          />
        </TabsContent>

        <TabsContent value="HANDWRITTEN_NOTE" className="mt-6">
          <PromptEditor
            type="HANDWRITTEN_NOTE"
            prompt={prompts.HANDWRITTEN_NOTE}
            onSave={handleSavePrompt}
            isSaving={savingType === 'HANDWRITTEN_NOTE'}
          />
        </TabsContent>

        <TabsContent value="BRANDING" className="mt-6">
          <BrandingSection superAdminToken={superAdminToken} />
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-base">💡 Wskazówki</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Prompty są używane przez AI (Claude) do przetwarzania notatek</p>
          <p>• Możesz używać placeholderów jak <code className="bg-background px-1 py-0.5 rounded">{'{{content}}'}</code> który zostanie zastąpiony treścią notatki</p>
          <p>• Wyłącz prompt jeśli chcesz tymczasowo zaprzestać przetwarzania AI dla danego typu notatek</p>
          <p>• Zmiany są zapisywane natychmiast po kliknięciu "Zapisz zmiany"</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromptsPage;
